import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { format } from 'date-fns';

import DeleteActionButton from '../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../components/designe/ActionButtons/EditActionButton';
import StartActionButton from '../../../components/designe/ActionButtons/StartActionButton';
import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import { successToastMessage } from '../../../components/layout/toast-root/toast';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import ClickableExercise from '../../../components/reusable/cliackable-exercise/ClickableExercise';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import SetOptionTable from '../../../components/reusable/set-options-table/SetOptionTable';
import { SetOptionTableMode } from '../../../components/reusable/set-options-table/interfaces';
import ViewField from '../../../components/reusable/view-field/ViewField';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { usePostPutProgramSet } from '../../../redux/api/programsAPI';
import {
  useGetTrainingActivityTypes,
  useStartTraining,
} from '../../../redux/api/trainingsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { Paths } from '../../../shared/paths';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { getTrainingActivityTypeName } from '../../../utils/training';
import { getSetKey } from '../../../utils/training/set-utils';
import { getProgramSetFromTrainingSet } from '../../../utils/training/set-utils';
import TrainingShareAccessTab from '../shared/training-share-access-tab/TrainingShareAccessTab';
import { useStyles } from './ViewTraining.styles';
import { useViewTrainingPage } from './useViewTrainingPage';

function ViewTraining(props) {
  const { params } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: activityTypes = [] } = useGetTrainingActivityTypes();

  const { classes } = useStyles(theme);

  const {
    training,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    trainingSetGroups,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleteTraining,
    isDeletingTraining,
  } = useViewTrainingPage(params.trainingId);

  const { navigate } = useNavigationStack();
  const [showStartConfirmation, setShowStartConfirmation] = useState(false);
  const [pendingStartId, setPendingStartId] = useState<string | null>(null);
  const [startTraining, { isLoading: isStartingTraining }] = useStartTraining();
  const [postPutProgramSet] = usePostPutProgramSet();
  const { showSaveSetButton } = useUserSetPreferences();
  const { data: user } = useGetMyDetails();

  const canConfigureAccess = !!training?.permissions?.canConfigureAccess;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !training && (isLoading || isFetching),
    hasData: Boolean(training),
    onRetry: handleRetry,
  });

  const tabsConfig = useMemo(() => {
    if (!canConfigureAccess) {
      return [];
    }
    return [
      {
        label: t('common.training'),
        path: Paths.trainings.toViewId(params.trainingId),
      },
      {
        label: t('trainings.finishedAccessTab'),
        path: Paths.trainings.toViewAccessId(params.trainingId),
      },
    ];
  }, [canConfigureAccess, params.trainingId, t]);

  const handleEdit = useCallback(
    () => navigate(Paths.trainings.toEditId(params.trainingId)),
    [navigate, params.trainingId],
  );

  const onSaveSet = useCallback(
    (set: ITrainingSet, title: string) => {
      const newProgramSet: IProgramSet = getProgramSetFromTrainingSet(set);

      newProgramSet.title = title;
      newProgramSet.ownerId = user?.id;
      newProgramSet.setNumber = 0;
      newProgramSet.orderNumber = 0;
      newProgramSet.exercise = null;

      return postPutProgramSet({
        setId: undefined,
        body: newProgramSet,
      })
        .unwrap()
        .then((response) => {
          successToastMessage(t('setTable.setSaved'));
          return response;
        })
        .catch((e) => {
          console.log(e);
          return null;
        });
    },
    [postPutProgramSet, user, t],
  );
  const availableActionsCount = useMemo(() => {
    let count = 0;
    if (training?.permissions?.canStart) count++;
    if (training?.permissions?.canEdit) count++;
    if (training?.permissions?.canDelete) count++;
    return count;
  }, [training]);

  const activityTypeName = getTrainingActivityTypeName(training, activityTypes);

  const mainDetails = training && (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        gap={1}
        flexDirection="column"
        className={classes.headerWrapper}>
        <Box
          display="flex"
          gap={0.5}
          justifyContent="space-between"
          alignItems="flex-start"
          minWidth={0}>
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            flex={1}
            minWidth={0}
            maxWidth={`calc(100% - ${availableActionsCount * 30}px - 10px)`}>
            <Typography variant="h1" className={classes.trainingTitle}>
              {training?.title}
            </Typography>
            {training?.isGenerated && (
              <Typography variant="primary-wrapped-sm">
                {t('trainings.aiGenerated')}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1} flexShrink={0}>
            {training?.permissions?.canStart && (
              <StartActionButton
                loading={
                  pendingStartId === params.trainingId && isStartingTraining
                }
                onClick={() => setShowStartConfirmation(true)}
              />
            )}
            {training?.permissions?.canEdit && (
              <EditActionButton onClick={handleEdit} />
            )}
            {training?.permissions?.canDelete && (
              <DeleteActionButton
                onClick={() => setShowDeleteConfirmation(true)}
              />
            )}
          </Box>
        </Box>
        {activityTypeName && (
          <ViewField
            value={activityTypeName}
            label={t('trainings.activityType')}
          />
        )}
        {training?.description && (
          <ViewField
            value={training?.description}
            label={t('common.description')}
          />
        )}
        {training?.planedStartDate && (
          <ViewField
            value={format(
              new Date(training.planedStartDate),
              'dd.MM.yyyy HH:mm',
            )}
            label={t('trainings.planedStartDate')}
          />
        )}
      </Box>
      <Box mt={1} paddingBottom={1}>
        {trainingSetGroups &&
          Object.values(trainingSetGroups).map((setGroup) => (
            <Box key={getSetKey(setGroup[0])} className={classes.groupWrapper}>
              {setGroup.map((set) => (
                <Box
                  key={set.id}
                  display={'flex'}
                  flexDirection="column"
                  gap={0.5}
                  className={classes.setWrapper}>
                  <ClickableExercise
                    exercise={set.exercise}
                    fromTrainingId={params.trainingId}
                  />
                  <Box marginTop={1}>
                    <SetOptionTable
                      set={set}
                      mode={SetOptionTableMode.training}
                      showSetsColumn={false}
                      showRestColumn={true}
                      canSaveSet={showSaveSetButton}
                      onSaveSet={(title) => onSaveSet(set, title)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
      </Box>
    </Box>
  );

  return (
    <PageContainer
      title={t('common.training')}
      backButton
      onRefresh={() => void refetch()}>
      {training && (
        <>
          {canConfigureAccess && <AppTabs config={tabsConfig} />}
          <Box marginTop={canConfigureAccess ? 1 : 0}>
            <Routes>
              <Route index element={mainDetails} />
              {canConfigureAccess && (
                <Route
                  path="access"
                  element={
                    <TrainingShareAccessTab
                      training={training}
                      trainingId={params.trainingId}
                      isFetching={isFetching}
                      redirectTo={Paths.trainings.toViewId(params.trainingId)}
                    />
                  }
                />
              )}
              <Route
                path="*"
                element={
                  <AppNavigate
                    to={Paths.trainings.toViewId(params.trainingId)}
                    replace
                  />
                }
              />
            </Routes>
          </Box>
        </>
      )}

      <ConfirmDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title={t('common.delete')}
        onSubmit={() => deleteTraining()}
        isLoading={isDeletingTraining}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>

      <ConfirmDialog
        open={showStartConfirmation}
        onClose={() => setShowStartConfirmation(false)}
        title={t('trainings.startQuestion')}
        onSubmit={() => {
          setPendingStartId(params.trainingId);
          return startTraining(params.trainingId)
            .unwrap()
            .then(() => {
              setShowStartConfirmation(false);
              navigate(Paths.trainings.toCurrent(params.trainingId), {
                replace: true,
              });
            })
            .catch((e) => {
              console.log(e);
            })
            .finally(() => {
              setPendingStartId(null);
            });
        }}
        isLoading={isStartingTraining}>
        {t('trainings.startQuestionMessage')}
      </ConfirmDialog>
    </PageContainer>
  );
}

export default withRouter(ViewTraining);
